import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings as SettingsIcon, User, Building, Mail, Bell, Shield,
  Palette, Database, Key, Save, Check, AlertTriangle, Eye, EyeOff,
  Globe, FileText, PenTool, CreditCard, ChevronRight, Loader2
} from 'lucide-react';
import { useAuth } from '../store/authStore';

type SettingsTab = 'profile' | 'firm' | 'notifications' | 'integrations' | 'security' | 'appearance';

export function Settings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'firm', label: 'Firm Settings', icon: Building },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'integrations', label: 'Integrations', icon: Key },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'appearance', label: 'Appearance', icon: Palette },
  ] as const;

  const handleSave = async () => {
    setSaving(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <SettingsIcon className="w-7 h-7 text-accent-400" />
            Settings
          </h1>
          <p className="text-primary-400 mt-1">Manage your account and preferences</p>
        </div>
        
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : saved ? (
            <Check className="w-5 h-5" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-64 shrink-0">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-accent-500/20 text-accent-400'
                    : 'text-primary-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
                {activeTab === tab.id && <ChevronRight className="w-4 h-4 ml-auto" />}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-primary-800/50 border border-primary-700 rounded-xl p-6"
          >
            {activeTab === 'profile' && <ProfileSettings email={user?.email || ''} />}
            {activeTab === 'firm' && <FirmSettings />}
            {activeTab === 'notifications' && <NotificationSettings />}
            {activeTab === 'integrations' && <IntegrationSettings />}
            {activeTab === 'security' && <SecuritySettings />}
            {activeTab === 'appearance' && <AppearanceSettings />}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

// Profile Settings
function ProfileSettings({ email }: { email: string }) {
  const [firstName, setFirstName] = useState('Seth');
  const [lastName, setLastName] = useState('Goettelman');
  const [phone, setPhone] = useState('(920) 555-1234');
  const [title, setTitle] = useState('Attorney at Law');

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Profile Settings</h2>
      
      <div className="flex items-center gap-6">
        <div className="w-20 h-20 rounded-full bg-accent-500/20 flex items-center justify-center">
          <span className="text-2xl text-accent-400 font-bold">SG</span>
        </div>
        <div>
          <button className="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600">
            Change Photo
          </button>
          <p className="text-sm text-primary-400 mt-1">JPG, PNG up to 5MB</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-primary-400 mb-2">First Name</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
          />
        </div>
        <div>
          <label className="block text-sm text-primary-400 mb-2">Last Name</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
          />
        </div>
        <div>
          <label className="block text-sm text-primary-400 mb-2">Email</label>
          <input
            type="email"
            value={email}
            disabled
            className="w-full px-4 py-2 bg-primary-900/50 border border-primary-700 text-primary-400 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm text-primary-400 mb-2">Phone</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm text-primary-400 mb-2">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
          />
        </div>
      </div>
    </div>
  );
}

// Firm Settings
function FirmSettings() {
  const [firmName, setFirmName] = useState('Goettelman Law');
  const [address, setAddress] = useState('123 Main Street, Suite 200');
  const [city, setCity] = useState('Sturgeon Bay');
  const [state, setState] = useState('Wisconsin');
  const [zip, setZip] = useState('54235');
  const [firmPhone, setFirmPhone] = useState('(920) 555-1234');
  const [firmEmail, setFirmEmail] = useState('info@goettelmanlaw.com');
  const [website, setWebsite] = useState('https://goettelmanlaw.com');

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Firm Settings</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-primary-400 mb-2">Firm Name</label>
          <input
            type="text"
            value={firmName}
            onChange={(e) => setFirmName(e.target.value)}
            className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
          />
        </div>
        
        <div>
          <label className="block text-sm text-primary-400 mb-2">Address</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-primary-400 mb-2">City</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
            />
          </div>
          <div>
            <label className="block text-sm text-primary-400 mb-2">State</label>
            <input
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
            />
          </div>
          <div>
            <label className="block text-sm text-primary-400 mb-2">ZIP Code</label>
            <input
              type="text"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-primary-400 mb-2">Phone</label>
            <input
              type="tel"
              value={firmPhone}
              onChange={(e) => setFirmPhone(e.target.value)}
              className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
            />
          </div>
          <div>
            <label className="block text-sm text-primary-400 mb-2">Email</label>
            <input
              type="email"
              value={firmEmail}
              onChange={(e) => setFirmEmail(e.target.value)}
              className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-primary-400 mb-2">Website</label>
          <input
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
          />
        </div>
      </div>
    </div>
  );
}

// Notification Settings
function NotificationSettings() {
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [documentGenerated, setDocumentGenerated] = useState(true);
  const [signatureCompleted, setSignatureCompleted] = useState(true);
  const [clientPortalActivity, setClientPortalActivity] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(false);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Notification Settings</h2>
      
      <div className="space-y-4">
        <ToggleSetting
          label="Email Notifications"
          description="Receive notifications via email"
          enabled={emailNotifications}
          onChange={setEmailNotifications}
        />
        
        <hr className="border-primary-700" />

        <ToggleSetting
          label="Document Generated"
          description="Notify when documents are generated"
          enabled={documentGenerated}
          onChange={setDocumentGenerated}
        />
        
        <ToggleSetting
          label="Signature Completed"
          description="Notify when e-signatures are completed"
          enabled={signatureCompleted}
          onChange={setSignatureCompleted}
        />
        
        <ToggleSetting
          label="Client Portal Activity"
          description="Notify when clients access the portal"
          enabled={clientPortalActivity}
          onChange={setClientPortalActivity}
        />
        
        <ToggleSetting
          label="Weekly Digest"
          description="Receive a weekly summary of activity"
          enabled={weeklyDigest}
          onChange={setWeeklyDigest}
        />
      </div>
    </div>
  );
}

// Integration Settings
function IntegrationSettings() {
  const integrations = [
    {
      id: 'docusign',
      name: 'DocuSign',
      description: 'E-signature integration',
      icon: PenTool,
      connected: false,
    },
    {
      id: 'hellosign',
      name: 'Dropbox Sign',
      description: 'E-signature integration',
      icon: PenTool,
      connected: false,
    },
    {
      id: 'clio',
      name: 'Clio',
      description: 'Practice management sync',
      icon: Database,
      connected: false,
    },
    {
      id: 'quickbooks',
      name: 'QuickBooks',
      description: 'Billing integration',
      icon: CreditCard,
      connected: false,
    },
    {
      id: 'gdrive',
      name: 'Google Drive',
      description: 'Document storage',
      icon: FileText,
      connected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Integrations</h2>
      
      <div className="space-y-4">
        {integrations.map((integration) => (
          <div
            key={integration.id}
            className="flex items-center justify-between p-4 bg-primary-900/50 rounded-lg border border-primary-700"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary-700 flex items-center justify-center">
                <integration.icon className="w-5 h-5 text-primary-300" />
              </div>
              <div>
                <p className="font-medium text-white">{integration.name}</p>
                <p className="text-sm text-primary-400">{integration.description}</p>
              </div>
            </div>
            
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                integration.connected
                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                  : 'bg-primary-700 text-primary-300 hover:bg-primary-600'
              }`}
            >
              {integration.connected ? 'Connected' : 'Connect'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Security Settings
function SecuritySettings() {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [twoFactor, setTwoFactor] = useState(false);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Security Settings</h2>
      
      {/* Change Password */}
      <div className="space-y-4">
        <h3 className="font-medium text-primary-300">Change Password</h3>
        
        <div className="relative">
          <label className="block text-sm text-primary-400 mb-2">Current Password</label>
          <input
            type={showCurrentPassword ? 'text' : 'password'}
            className="w-full px-4 py-2 pr-10 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
          />
          <button
            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            className="absolute right-3 top-9 text-primary-400 hover:text-white"
          >
            {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        <div className="relative">
          <label className="block text-sm text-primary-400 mb-2">New Password</label>
          <input
            type={showNewPassword ? 'text' : 'password'}
            className="w-full px-4 py-2 pr-10 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
          />
          <button
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute right-3 top-9 text-primary-400 hover:text-white"
          >
            {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        <div>
          <label className="block text-sm text-primary-400 mb-2">Confirm New Password</label>
          <input
            type="password"
            className="w-full px-4 py-2 bg-primary-900 border border-primary-600 text-white rounded-lg focus:border-accent-500"
          />
        </div>

        <button className="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600">
          Update Password
        </button>
      </div>

      <hr className="border-primary-700" />

      {/* Two-Factor */}
      <ToggleSetting
        label="Two-Factor Authentication"
        description="Add an extra layer of security to your account"
        enabled={twoFactor}
        onChange={setTwoFactor}
      />

      <hr className="border-primary-700" />

      {/* Sessions */}
      <div>
        <h3 className="font-medium text-primary-300 mb-4">Active Sessions</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-primary-900/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-primary-400" />
              <div>
                <p className="text-sm text-white">Chrome on macOS</p>
                <p className="text-xs text-primary-500">Current session • Sturgeon Bay, WI</p>
              </div>
            </div>
            <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded">Active</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Appearance Settings
function AppearanceSettings() {
  const [theme, setTheme] = useState<'dark' | 'light' | 'system'>('dark');
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [compactMode, setCompactMode] = useState(false);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Appearance Settings</h2>
      
      {/* Theme */}
      <div>
        <label className="block text-sm text-primary-400 mb-3">Theme</label>
        <div className="flex gap-3">
          {(['dark', 'light', 'system'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTheme(t)}
              className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium capitalize transition-colors ${
                theme === t
                  ? 'border-accent-500 bg-accent-500/10 text-accent-400'
                  : 'border-primary-600 text-primary-300 hover:border-primary-500'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Font Size */}
      <div>
        <label className="block text-sm text-primary-400 mb-3">Font Size</label>
        <div className="flex gap-3">
          {(['small', 'medium', 'large'] as const).map((size) => (
            <button
              key={size}
              onClick={() => setFontSize(size)}
              className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium capitalize transition-colors ${
                fontSize === size
                  ? 'border-accent-500 bg-accent-500/10 text-accent-400'
                  : 'border-primary-600 text-primary-300 hover:border-primary-500'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      <hr className="border-primary-700" />

      <ToggleSetting
        label="Compact Mode"
        description="Reduce spacing and padding throughout the interface"
        enabled={compactMode}
        onChange={setCompactMode}
      />
    </div>
  );
}

// Toggle Setting Component
function ToggleSetting({
  label,
  description,
  enabled,
  onChange,
}: {
  label: string;
  description: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="font-medium text-white">{label}</p>
        <p className="text-sm text-primary-400">{description}</p>
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          enabled ? 'bg-accent-500' : 'bg-primary-600'
        }`}
      >
        <span
          className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
            enabled ? 'left-7' : 'left-1'
          }`}
        />
      </button>
    </div>
  );
}
